import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { DataPeriod } from './enums';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export { dayjs };

//for report timeRange
export function getTimePeriod(
  type: DataPeriod,
  offset: string,
  format = 'YYYY/MM/DD'
) {
  let start: Dayjs, end: Dayjs;
  let baseDay: dayjs.Dayjs;
  let tzOffset = offset;
  switch (type) {
    case DataPeriod.LastDay:
      baseDay = dayjs().utcOffset(offset).startOf('day');
      start = end = baseDay.add(-1, 'day');
      break;
    case DataPeriod.LastWeek:
      baseDay = dayjs().utcOffset(offset).startOf('week');
      end = baseDay.add(-1, 'day');
      start = baseDay.add(-1, 'week');
      break;
    case DataPeriod.LastMonth:
      baseDay = dayjs().utcOffset(offset).startOf('month');
      end = baseDay.add(-1, 'day');
      start = baseDay.add(-1, 'month');
      break;
    case DataPeriod.Last6Months:
      baseDay = dayjs().utcOffset(offset).startOf('month');
      end = baseDay.add(-1, 'day');
      start = baseDay.add(-6, 'month');
      tzOffset = '+00:00';
      break;
    case DataPeriod.LastYear:
      baseDay = dayjs().utcOffset(offset).startOf('year');
      end = baseDay.add(-1, 'day');
      start = baseDay.add(-1, 'year');
      tzOffset = '+00:00';
      break;
    default:
      return null;
  }
  return {
    start: start.format(format),
    end: end.format(format),
    timezoneOffset: tzOffset,
  };
}

export const getUtcTime = (
  timestamp: string | number | undefined,
  offset = '+00:00',
  format = 'YYYY-MM-DD hh:mm (UTCZ)'
) => {
  return timestamp
    ? dayjs.utc(timestamp).utcOffset(offset).format(format)
    : dayjs.utc().utcOffset(offset).format(format);
};

export const utcOffsetToTimezone = (offset: string) => {
  return dayjs().utcOffset(offset).tz();
};

export const timezoneToUtcOffsetZ = (tz: string) => {
  return dayjs().tz(tz).format('Z');
};

export function getUtcTimestamp(timestamp = '', offset = '+00:00') {
  return dayjs.utc(timestamp).utcOffset(offset).valueOf();
}

export function getLocalUtcOffset() {
  return dayjs().format('Z');
}

export const getLocalTime = (format = 'YYYY-MM-DD hh:mm (UTCZ)') => {
  return dayjs().format(format);
};

//convert '+08:00' to 480
export const convertTimezoneOffsetToMinutes = (offset: string): number => {
  const parts = offset.split(':');
  return parseInt(parts[0]) * 60 + parseInt(parts[1]) * 1;
};

export const convertLocalTimeToUtcTimeStr = (
  timeStr = '',
  // localFormat = 'YYYY-MM-DD[T]HH:mm',
  utcFormat = 'YYYY-MM-DD[T]HH:mm'
) => {
  return dayjs(timeStr).utc().format(utcFormat);
};

export const convertUtcTimeStringToLocalTime = (
  timeStr = '',
  utcFormat = 'YYYY-MM-DD[T]HH:mm',
  localFormat = 'YYYY-MM-DD[T]HH:mm'
) => {
  return dayjs.utc(timeStr, utcFormat).local().format(localFormat);
};

export const convertLocalTimeToUtcTimeZ = (t = '') => {
  return dayjs(t).utc().format('YYYY-MM-DD HH:mm:ssZ');
};

export const convertLocalTimeToUtcTimeZZ = (t = '') => {
  return dayjs(t).utc().format('YYYY-MM-DD HH:mm:ssZZ');
};

/**
 * @description check if value is Date format or not
 * @param value if value type is string, its format must be yyyy-MM-dd
 * @returns
 */
export const isValidData = (value: Date | string) => {
  if (value instanceof Date) {
    return !!value.getTime();
  }
  const pattern = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
  return !!String(value).match(pattern);
};

/**
 * @description check if value is Time format or not
 * @param value value format must be HH:mm:ss
 * @returns
 */
export const isValidTime = (value: string) => {
  const pattern = /^(?:2[0-3]|[01]?[0-9]):[0-5][0-9]:[0-5][0-9]$/;
  return !!String(value).match(pattern);
};

/**
 * @description calc the whole day between two date
 */
export const calcDaysBetween = (date1: Date, date2: Date) => {
  date1.setHours(0, 0, 0, 0);
  date2.setHours(0, 0, 0, 0);
  const time1 = date1.getTime();
  const time2 = date2.getTime();
  const diff = Math.abs(time1 - time2);
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return days;
};

/**
 * @description get time by dashboard timezone offset, not system
 */
export const getUserTime = (userOffset: string) => {
  const diffMinutes =
    convertTimezoneOffsetToMinutes(userOffset) - dayjs().utcOffset();
  return dayjs().add(diffMinutes, 'minute').toDate();
};

export default {
  convertLocalTimeToUtcTimeZ,
  getUtcTime,
  getLocalTime,
  getUtcTimestamp,
  getTimePeriod,
  convertUtcTimeStringToLocalTime,
  convertLocalTimeToUtcTimeStr,
  convertTimezoneOffsetToMinutes,
  isValidData,
  isValidTime,
  calcDaysBetween,
};
